/**
 * @file ServiceCostsCard.tsx
 * @brief Vendor costs: the `pricing_config` rates, one tab per vendor.
 *
 * @details
 * Each row is a vendor rate stored in US cents per unit and read by the
 * per-step COGS ledger. The page shows it in dollars with a human name, a
 * plain unit and (where it applies) what it costs us per hour of use. Rates
 * we pay with no row are listed as "Not set" (read-only, see
 * serviceCostCatalog.ts). Save sends the open tab's changes only, after a
 * "Save will change" list, like the plan prices page.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock flush layout
 * @updated 22-10-2026 — vendor tabs, human names, $ units, per-tab save (W2)
 */

import { useMemo, useState } from "react";
import { Cpu, LoaderCircle, Save } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/shared/theme/cn";
import { FieldStyleContext, bareFieldLineClass } from "@/components/ui/fieldStyle";
import { Label } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useUpdatePricingConfig } from "../api";
import { BILLING_SECTIONS } from "../billingNav";
import type { PricingConfigResponse, PricingEntry } from "../schemas";
import {
  COST_VENDORS,
  HINT_GBP_PER_USD,
  MISSING_RATES,
  SERVICE_META,
  type CostVendorId,
  centsToDollarString,
  dollarStringToCents,
  unitLabel,
  usageHint,
  vendorForKey,
} from "../serviceCostCatalog";

function formatUpdated(iso?: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatUpdatedShort(ms: number | null): string {
  if (ms === null) return "Never updated";
  return `Updated ${new Date(ms).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}

interface Row {
  key: string;
  entry: PricingEntry;
}

interface ServiceCostsCardProps {
  data: PricingConfigResponse | undefined;
  isLoading?: boolean;
}

export function ServiceCostsCard({ data, isLoading }: ServiceCostsCardProps) {
  const [vendor, setVendor] = useState<CostVendorId>("openai");
  // Only edited rows live here (dollars as typed), so a refetch after one
  // tab's save never wipes another tab's unsaved edits.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const update = useUpdatePricingConfig();

  const byVendor = useMemo(() => {
    const groups = new Map<CostVendorId, Row[]>(
      COST_VENDORS.map((v) => [v.id, []]),
    );
    for (const [key, entry] of Object.entries(data?.pricing ?? {})) {
      groups.get(vendorForKey(key))?.push({ key, entry });
    }
    for (const rows of groups.values()) {
      rows.sort((a, b) =>
        (SERVICE_META[a.key]?.name ?? a.key).localeCompare(
          SERVICE_META[b.key]?.name ?? b.key,
        ),
      );
    }
    return groups;
  }, [data]);

  const lastUpdated = (rows: Row[]): number | null => {
    let max: number | null = null;
    for (const { entry } of rows) {
      const t = entry.updated_at ? new Date(entry.updated_at).getTime() : NaN;
      if (!Number.isNaN(t) && (max === null || t > max)) max = t;
    }
    return max;
  };

  // Changes per vendor: valid edits that differ from the stored value.
  const changes = useMemo(() => {
    const out = new Map<
      CostVendorId,
      { key: string; oldCents: number; newCents: number }[]
    >();
    const invalid = new Set<string>();
    for (const [key, typed] of Object.entries(draft)) {
      const stored = data?.pricing?.[key]?.cost_per_unit_cents;
      if (typeof stored !== "number") continue;
      const cents = dollarStringToCents(typed);
      if (cents === null) {
        invalid.add(key);
        continue;
      }
      if (cents === stored) continue;
      const v = vendorForKey(key);
      out.set(v, [...(out.get(v) ?? []), { key, oldCents: stored, newCents: cents }]);
    }
    return { byVendor: out, invalid };
  }, [draft, data]);

  const rows = byVendor.get(vendor) ?? [];
  const missing = MISSING_RATES.filter((m) => m.vendor === vendor);
  const tabChanges = changes.byVendor.get(vendor) ?? [];
  const tabInvalid = rows.filter((r) => changes.invalid.has(r.key)).length;
  const vendorLabel = COST_VENDORS.find((v) => v.id === vendor)?.label ?? "";

  const handleSave = async () => {
    if (tabChanges.length === 0 || tabInvalid > 0) return;
    const pricing: Record<string, number> = {};
    for (const c of tabChanges) pricing[c.key] = c.newCents;
    try {
      await update.mutateAsync({ pricing });
      setDraft((d) => {
        const next = { ...d };
        for (const c of tabChanges) delete next[c.key];
        return next;
      });
      toast({
        variant: "success",
        title: `${vendorLabel} costs updated`,
        description: `Saved ${tabChanges.length} rate${tabChanges.length === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <>
      <GridBand
        id={BILLING_SECTIONS.serviceCosts}
        className="bg-muted/25 py-2.5"
      >
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Service costs
        </p>
        <h3 className="mt-0.5 flex items-center gap-2 text-base font-semibold tracking-tight">
          <Cpu className="h-4 w-4 text-primary" />
          Vendor costs
        </h3>
        <p className="mt-0.5 max-w-3xl text-sm text-muted-foreground">
          What each vendor charges us, used to work out the cost of every
          meeting, note and interpretation. Vendors bill in{" "}
          <strong className="font-medium text-foreground">US dollars</strong>,
          so rates are entered in $ (stored as US cents). The ≈ £ lines use
          £{HINT_GBP_PER_USD} per $1 and are a guide only.
        </p>
      </GridBand>

      <GridCells
        className="grid-cols-2 sm:grid-cols-4 lg:grid-cols-7"
        role="tablist"
        aria-label="Vendors"
      >
        {COST_VENDORS.map((v) => {
          const vRows = byVendor.get(v.id) ?? [];
          const vMissing = MISSING_RATES.filter((m) => m.vendor === v.id).length;
          const pending = changes.byVendor.get(v.id)?.length ?? 0;
          const active = v.id === vendor;
          return (
            <GridCell key={v.id} padded={false} className="min-w-0">
              <button
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setVendor(v.id)}
                className={cn(
                  "flex h-full w-full flex-col items-start gap-0.5 px-3 py-3 text-left transition-colors md:px-4",
                  active
                    ? "bg-brand-soft text-brand-soft-foreground"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                )}
              >
                <span
                  className={cn(
                    "text-sm",
                    active ? "font-semibold" : "font-medium text-foreground",
                  )}
                >
                  {v.label}
                </span>
                <span className="text-xs">
                  {isLoading
                    ? "…"
                    : `${vRows.length} rate${vRows.length === 1 ? "" : "s"}${
                        vMissing ? ` · ${vMissing} not set` : ""
                      }`}
                  {pending > 0 ? (
                    <span className="font-medium text-primary">
                      {" "}· {pending} edited
                    </span>
                  ) : null}
                </span>
                <span className="text-[11px] opacity-80">
                  {isLoading ? "" : formatUpdatedShort(lastUpdated(vRows))}
                </span>
              </button>
            </GridCell>
          );
        })}
      </GridCells>

      <GridBand className="flex flex-wrap items-center justify-between gap-3 py-2.5">
        <p className="text-sm font-medium">
          {vendorLabel}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {tabInvalid > 0
              ? `Fix ${tabInvalid} rate${tabInvalid === 1 ? "" : "s"} first.`
              : tabChanges.length
                ? `${tabChanges.length} pending change${tabChanges.length === 1 ? "" : "s"} in this tab.`
                : "No pending changes."}
          </span>
        </p>
        <Button
          onClick={handleSave}
          disabled={tabChanges.length === 0 || tabInvalid > 0 || update.isPending}
        >
          {update.isPending ? (
            <>
              <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {tabChanges.length
                ? `Save ${tabChanges.length} ${vendorLabel} change${tabChanges.length === 1 ? "" : "s"}`
                : "No changes"}
            </>
          )}
        </Button>
      </GridBand>

      {tabChanges.length ? (
        <GridCell>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Save will change
          </p>
          <ul className="space-y-1 font-mono text-xs">
            {tabChanges.map((c) => (
              <li key={c.key} className="break-all">
                {c.key}:{" "}
                <span className="text-muted-foreground">
                  ${centsToDollarString(c.oldCents)} ({c.oldCents}¢)
                </span>{" "}
                →{" "}
                <span className="font-semibold">
                  ${centsToDollarString(c.newCents)} ({c.newCents}¢)
                </span>
              </li>
            ))}
          </ul>
        </GridCell>
      ) : null}

      <GridCell padded={false}>
        {isLoading ? (
          <div className="space-y-3 p-4 md:p-5">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : rows.length === 0 && missing.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No {vendorLabel} rates.
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {rows.map(({ key, entry }) => {
              const meta = SERVICE_META[key];
              const typed = draft[key];
              const value = typed ?? centsToDollarString(entry.cost_per_unit_cents);
              const cents = typed === undefined ? entry.cost_per_unit_cents : dollarStringToCents(typed);
              const hint =
                meta && cents !== null
                  ? usageHint(meta.basis, entry.unit_type, cents)
                  : null;
              const invalid = changes.invalid.has(key);
              return (
                <div
                  key={key}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:flex-nowrap md:px-5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{meta?.name ?? key}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {unitLabel(entry.unit_type)}
                      {meta?.note ? ` · ${meta.note}` : ""}
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] text-muted-foreground/70">
                      {key} · updated {formatUpdated(entry.updated_at)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-end gap-0.5 sm:w-auto">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Label htmlFor={`svc-${key}`} className="sr-only">
                        {meta?.name ?? key}, {unitLabel(entry.unit_type)}
                      </Label>
                      {/* The app's bare field in a row (DES 01-10-2026: "poora
                          billing page ... theek kar do"): no box, the value on a
                          hairline. */}
                      <FieldStyleContext.Provider value={{ bare: true }}>
                        <div className={cn("w-28", bareFieldLineClass, "pb-1")}>
                          <Input
                            id={`svc-${key}`}
                            inputMode="decimal"
                            spellCheck={false}
                            className="text-right tabular-nums"
                            value={value}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, [key]: e.target.value }))
                            }
                          />
                        </div>
                      </FieldStyleContext.Provider>
                    </div>
                    <span
                      className={cn(
                        "text-[11px]",
                        invalid ? "text-destructive" : "text-muted-foreground",
                      )}
                    >
                      {invalid ? "Enter an amount in $" : hint ?? " "}
                    </span>
                  </div>
                </div>
              );
            })}
            {missing.map((m) => (
              <div
                key={m.key}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:flex-nowrap md:px-5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{m.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {m.unit} · {m.why}
                  </p>
                  <p className="mt-0.5 font-mono text-[11px] text-muted-foreground/70">
                    no row (proposed: {m.key})
                  </p>
                </div>
                <Badge variant="muted" className="shrink-0 font-normal">
                  Not set
                </Badge>
              </div>
            ))}
          </div>
        )}
      </GridCell>
    </>
  );
}
